<template>
    <div class="ticket notranslate">
        <div class="ticket-title">
            <div class="ticket-title-group">
                <span v-if="singleTicket.isLive" class="ticket-livetag">
                    {{ getTranslationBySite('live') }}
                </span>
                <SvgIcon v-bind:iconName="iconName" />
                <span class="ticket-league" v-bind:class="{ _live: singleTicket.isLive }">
                    {{ leagueName }}
                </span>
            </div>
            <div
                class="ticket-close JARVIS_BeTticket_Btn_DeleteTicket"
                v-on:click="removeBetSlip"
            >
                <SvgIcon iconName="systemIcon_close" />
            </div>
        </div>
        <div class="ticket-odd">
            <div class="ticket-match">
                <span v-show="isShowLiveScore" class="ticket-live-score">
                    [{{ singleTicket.odds.eventResult.liveHomeScore }}:{{ singleTicket.odds.eventResult.liveAwayScore }}]
                </span>
                <span class="ticket-team">
                    {{ homeTeam }}
                </span>
                -
                <span class="ticket-team">
                    {{ awayTeam }}
                </span>
            </div>
            <div class="ticket-odd-content">
                <div class="ticket-odd-info">
                    <div class="ticket-marketType">
                        {{ optionName }}
                        <SvgIcon v-if="isDisplayCashOutIcon" iconName="sportIcon_cashOut" />
                    </div>
                    <div class="ticket-oddType">
                        {{ option }}
                    </div>
                </div>
                <div class="ticket-odd-main">
                    <div class="ticket-oddPoint">
                        {{ toFixedDecimalPoint(point, 2) }}
                    </div>
                    <div class="ticket-oddValue" v-bind:class="{ _negative: isOddsNegative }">
                        {{ displayPrice }}
                    </div>
                </div>
                <div
                    class="ticket-refresh-btn"
                    v-bind:class="{ _refreshing: isRefreshing }"
                    v-on:click="refresh(false, true)"
                >
                    <SvgIcon iconName="systemIcon_refresh_centered" style="fill: #000000; font-size: 1.8rem;" />
                </div>
            </div>

            <div v-if="isCorrectScoreAos" class="ticket-odd-aos">
                {{ i18n.t('odds_option_cs_excluding') + singleTicket.betCondition }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, onMounted, ref, toRefs } from 'vue';
    import { SportType } from '@/interface/enum';
    import { hasLastStake } from '@/libraries/betSlip/lastStakeStorage';
    import { useParlayCashOut, useSingleBetCashOut } from '@/libraries/composables/useBetSlipCashOut';
    import { useEuroTicketDisplay } from '@/libraries/composables/useEuroTicketDisplay';
    import type { IEuroTicket } from '@/libraries/euroTicket';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { i18n } from '@/libraries/i18n';
    import { useOddsSubscriptionCallback } from '@/libraries/oddsApi/composables/useOddsSubscriptionCallback';
    import { invertCapitalize } from '@/libraries/string';
    import { toFixedDecimalPoint } from '@/libraries/utils/text';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useSingleBetSlipStore } from '@/stores/betSlip/singleBetSlipStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    interface Props {
        singleTicket: IEuroTicket;
        isMixParlay: boolean;
    }

    const props = defineProps<Props>();
    const emit = defineEmits<{
        (e: 'refreshTicket'): void;
        (e: 'removeTicket'): void;
        (e: 'refresh', isFirstTime: boolean, isCallApi: boolean): void;
    }>();

    const { singleTicket, isMixParlay } = toRefs(props);
    const { setIsShowStakePad } = useBetSlipStore();
    const { updateTicket } = useSingleBetSlipStore();
    const { customerInfo } = storeToRefs(useAccountStore());

    const {
        leagueName, homeTeam, awayTeam, marketGroupName, option, point, isCorrectScoreAos, isOddsNegative, displayPrice, isShowLiveScore,
    } = useEuroTicketDisplay(singleTicket);

    const optionName = computed(() => `${marketGroupName.value} ${i18n.t(singleTicket.value.marketDisplayTypeName)}`.trim());

    const iconName = computed(() => `sportIcon_${invertCapitalize(SportType[singleTicket.value.sportType])}`);

    const betSlipCashOut = computed(() => ({
        sportType: singleTicket.value.sportType,
        isMainMarket: singleTicket.value.odds.marketGroup.id === 0,
        isLive: singleTicket.value.isLive,
        leagueId: singleTicket.value.leagueId,
        marketType: singleTicket.value.marketType,
    }));
    const { isDisplayCashOutIcon: isSingleBetCashOutSupported } = useSingleBetCashOut(betSlipCashOut);
    const { isDisplayCashOutIcon: isParlayCashOutSupported } = useParlayCashOut(
        computed(() => [betSlipCashOut.value]),
    );
    const isDisplayCashOutIcon = computed(() => (
        isMixParlay.value ? isParlayCashOutSupported.value : isSingleBetCashOutSupported.value
    ));

    const isRefreshing = ref(false);
    function refresh(isFirstTime: boolean, isCallApi: boolean) {
        emit('refresh', isFirstTime, isCallApi);

        isRefreshing.value = true;
        setTimeout(() => {
            isRefreshing.value = false;
        }, 1000);
    }

    function handleUpdate() {
        emit('refreshTicket');
    }

    function handleDelete(oddsId: number) {
        if (singleTicket.value.oddsId !== oddsId) return;

        if (props.isMixParlay) {
            emit('refreshTicket');
        } else {
            updateTicket({
                key: `${singleTicket.value.oddsId}-${singleTicket.value.option}`,
                messageKey: 'betSlip_errorMessage_oddsClosed',
            });
            setTimeout(() => {
                emit('removeTicket');
            }, 3000);
        }
    }

    function removeBetSlip() {
        emit('removeTicket');
    }

    onMounted(() => {
        const hasDefaultStake = hasLastStake();
        if (!hasDefaultStake) {
            setIsShowStakePad(false);
        }
    });

    const {
        onUpdated,
        onDeleted,
    } = useOddsSubscriptionCallback({
        eventId: singleTicket.value.eventId,
        oddsId: singleTicket.value.oddsId,
        isLive: singleTicket.value.isLive,
        isMixParlay: props.isMixParlay,
        priceStyle: customerInfo.value.priceStyle,
        isActive: ref(true),
        sportType: singleTicket.value.sportType,
        leagueSize: singleTicket.value.event.leagueSize,
    });
    onUpdated(handleUpdate);
    onDeleted(handleDelete);
</script>

<style lang="scss" scoped>
    @import "@/scss/components/ticket";
</style>
